import { toast } from "sonner";
import { Calendar, Mail, Video, FileText } from "lucide-react";

/**
 * IntegrationActions — small, reusable demo buttons that simulate Outlook/Teams flows.
 * No real API; on click a realistic toast is shown.
 */

function fakeOutlookEvent({ title, attendees, when, location }) {
  toast.success("📅 Outlook takvim daveti oluşturuldu", {
    description: `“${title}” • ${when} • ${location || "Online"}\nKatılımcılar: ${attendees}`,
    duration: 4500,
    classNames: { toast: "border-l-4 border-l-[#0072C6]" },
  });
}

function fakeOutlookEmail({ subject, to }) {
  toast.success("📧 Outlook e-postası taslağa kaydedildi", {
    description: `Konu: “${subject}”\nAlıcı: ${to}\nGönderim için Outlook'tan onaylayın.`,
    duration: 4500,
    classNames: { toast: "border-l-4 border-l-[#0072C6]" },
  });
}

function fakeTeamsMeeting({ title, attendees, when }) {
  toast.success("🟦 Teams toplantısı oluşturuldu", {
    description: `“${title}” • ${when}\nKatılımcılar: ${attendees}\nLink Outlook davetiyesine eklendi.`,
    duration: 4500,
    classNames: { toast: "border-l-4 border-l-[#4B53BC]" },
  });
}

function fakeTeamsTranscript({ when }) {
  toast.info("🟦 Teams transkripti analiz ediliyor", {
    description: `${when} tarihli toplantı için kayıt çekiliyor… AI özet 30 saniye içinde hazır olacak.`,
    duration: 4500,
    classNames: { toast: "border-l-4 border-l-[#4B53BC]" },
  });
}

const baseBtn =
  "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-[0.7rem] font-medium transition";

/**
 * Mentor onayı sonrası — takvim daveti + Teams toplantısı kombosu.
 */
export function MentorApprovedActions({ mentor, mentee, cadence = "haftalik" }) {
  const cadenceLabel = cadence === "haftalik" ? "Haftalık" : cadence === "iki-haftalik" ? "İki haftada bir" : "Aylık";
  const title = `${mentor.split(" ")[0]} ↔ ${mentee.split(" ")[0]} Mentorluk Görüşmesi`;
  const when = `${cadenceLabel} • Perşembe 16:00–16:45`;
  const onOutlook = () => fakeOutlookEvent({ title, attendees: `${mentor}, ${mentee}`, when, location: "Teams" });
  const onTeams = () => fakeTeamsMeeting({ title, attendees: `${mentor}, ${mentee}`, when });
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid="mentor-approved-actions">
      <button
        type="button"
        onClick={onOutlook}
        data-testid="action-outlook-calendar"
        className={`${baseBtn} border-[#0072C6]/30 bg-[#0072C6]/5 text-[#0072C6] hover:bg-[#0072C6]/10`}
      >
        <Calendar className="w-3.5 h-3.5" />
        Outlook Takvim Daveti
      </button>
      <button
        type="button"
        onClick={onTeams}
        data-testid="action-teams-meeting"
        className={`${baseBtn} border-[#4B53BC]/30 bg-[#4B53BC]/5 text-[#4B53BC] hover:bg-[#4B53BC]/10`}
      >
        <Video className="w-3.5 h-3.5" />
        Teams Toplantısı Oluştur
      </button>
    </div>
  );
}

/**
 * Eğitim önerisi gönderirken — Outlook e-posta + takvim hatırlatıcı.
 */
export function TrainingSuggestionIntegrations({ employeeName, title, suggester }) {
  const onEmail = () =>
    fakeOutlookEmail({
      subject: `${suggester} tarafından önerilen eğitim: ${title}`,
      to: employeeName,
    });
  const onReminder = () =>
    fakeOutlookEvent({
      title: `Eğitim başlangıcı: ${title}`,
      attendees: employeeName,
      when: "Önümüzdeki Pazartesi 09:00",
      location: "Self-paced",
    });
  return (
    <div className="flex flex-wrap gap-2" data-testid="training-suggestion-integrations">
      <button
        type="button"
        onClick={onEmail}
        data-testid="action-outlook-email"
        className={`${baseBtn} border-[#0072C6]/30 bg-[#0072C6]/5 text-[#0072C6] hover:bg-[#0072C6]/10`}
      >
        <Mail className="w-3.5 h-3.5" />
        Outlook&apos;tan E-posta
      </button>
      <button
        type="button"
        onClick={onReminder}
        data-testid="action-outlook-reminder"
        className={`${baseBtn} border-[#0072C6]/30 bg-[#0072C6]/5 text-[#0072C6] hover:bg-[#0072C6]/10`}
      >
        <Calendar className="w-3.5 h-3.5" />
        Takvim Hatırlatıcı
      </button>
    </div>
  );
}

/**
 * Toplantı notları için — Teams transkript özeti çek.
 */
export function MeetingTranscriptAction({ when = "Son toplantı" }) {
  return (
    <button
      type="button"
      onClick={() => fakeTeamsTranscript({ when })}
      data-testid="action-teams-transcript"
      className={`${baseBtn} border-[#4B53BC]/30 bg-[#4B53BC]/5 text-[#4B53BC] hover:bg-[#4B53BC]/10`}
    >
      <FileText className="w-3.5 h-3.5" />
      Teams&apos;den Transkript Özetini Çek
    </button>
  );
}
